import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/apiFetch';
import AuthService from '../auth/handleTokenProvider';
import { isAuth } from '../auth/isAuth';
import { routes } from '../router/routes';
import type { User } from '../types';

/**
 * Routes accessibles sans authentification (ex: page de connexion, formulaire
 * de réponse SMS envoyé aux psys). L'AuthProvider ne doit jamais rediriger
 * vers /login lorsque l'utilisateur consulte l'une de ces routes, sous peine
 * de casser leur accès (cf. bug du lien SMS renvoyant vers la connexion).
 */
const PUBLIC_ROUTES = [routes.login, routes.ReponseSMS];

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some((pattern) => matchPath(pattern, pathname) !== null);
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(isAuth()));
  const hasLoadedUserRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const logout = useCallback(() => {
    AuthService.logout();
    hasLoadedUserRef.current = false;
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    if (isPublicRoute(location.pathname)) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    if (!isAuth()) {
      setUser(null);
      setIsLoading(false);
      navigate('/login', { replace: true });
      return;
    }

    if (!hasLoadedUserRef.current) {
      setIsLoading(true);
    }

    const fetchedUser = (await apiFetch('/users/me')) as User | null;

    if (fetchedUser && fetchedUser['@id']) {
      hasLoadedUserRef.current = true;
      setUser(fetchedUser);
      if (fetchedUser.roles) {
        localStorage.setItem('REACT_ROLE', JSON.stringify(fetchedUser.roles));
      }
    } else {
      AuthService.logout();
      hasLoadedUserRef.current = false;
      setUser(null);
      navigate('/login', { replace: true });
    }

    setIsLoading(false);
  }, [location.pathname, navigate]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      setUser,
      refreshUser,
      logout,
    }),
    [user, isLoading, refreshUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
